import { makeObservable } from 'mobx'

import commonRoutes, {
  UrlParamKey,
} from '~/client/src/shared/constants/commonRoutes'
import Photo from '~/client/src/shared/models/Photo'
import InitialState from '~/client/src/shared/stores/InitialState'
import CommonStore, {
  IDisplayLoginViewOptions,
  IDisplayViewParams,
} from '~/client/src/shared/stores/ui/Common.store'
import {
  getProjectReturnTo,
  getReturnToFromUrl,
} from '~/client/src/shared/utils/loginPortal'

import LoginRootStore from '../LoginRoot.store'

export default class LoginCommonStore extends CommonStore {
  public constructor(
    protected state: InitialState,
    protected root: LoginRootStore,
  ) {
    super(state, root)
    makeObservable(this)
  }

  /**
   * Carry the `return_to` destination across in-bundle navigation
   * (STX1-540) centrally: the base `_displayView` rebuilds the route
   * from the route constant + explicit query params and drops
   * `location.search`, so without this every push toward the Redirector
   * / tenant routing would silently lose the destination. Re-injecting
   * here — once, for the whole login bundle — removes the per-call
   * fragility (a new display* method can't forget it). The URL is the
   * single carrier; router state would not survive the SSO hop or the
   * cross-origin invite-key redirect.
   *
   * The `/login` route is NOT special-cased out here: the unauth
   * bootstrap reaches `/login` via `displayLoginView({ preserveReturnTo:
   * true })`, and that hop must keep the `return_to` that arrived on the
   * URL — dropping it stranded the re-login on the picker. Fresh-login /
   * logout bounces instead reach `/login` via the base `super._displayView`
   * (see `displayLoginView`), which rebuilds the route and carries
   * nothing — so re-injection here only ever applies to context-carrying
   * navigation, never to a session-ending one.
   */
  public _displayView(route: string, params: IDisplayViewParams = {}) {
    const returnTo = getReturnToFromUrl()
    if (returnTo && !(UrlParamKey.ReturnTo in params)) {
      const withContext: IDisplayViewParams = { ...params }
      ;(withContext as Record<string, string>)[UrlParamKey.ReturnTo] = returnTo
      super._displayView(route, withContext)
      return
    }
    super._displayView(route, params)
  }

  public displayAuthSuccessRoute = () => {
    // Post-login landing = the User Portal (/portal). Routed through the
    // re-injecting _displayView so a return_to deep-link on the URL survives
    // and the /portal gate can fly the user straight over to the tenant.
    this._displayView(commonRoutes.PORTAL)
  }

  public displayLoginView = (
    options: IDisplayLoginViewOptions = {},
  ): boolean => {
    // In-app navigation on the login bundle (no full-page redirect): the
    // login routes render the form directly.
    //
    // Clean by default: route to `/login` via the base `super._displayView`,
    // which rebuilds the route from the constant and carries NOTHING from
    // the current URL. So logout and every other fresh-login bounce land
    // context-free automatically — even if a `return_to` (or any future
    // param) is still on the URL, e.g. the Redirector failure UI dispatches
    // LOGOUT at `/redirector?return_to=…`. No param enumeration, nothing to
    // revisit when new context is added (STX1-540).
    //
    // preserveReturnTo — only the unauth bootstrap opts in, to keep the
    // deep link alive for the post-login fly-over. It routes through the
    // re-injecting override so the `return_to` on the URL survives.
    if (options.preserveReturnTo) {
      this._displayView(commonRoutes.LOGIN)
    } else {
      super._displayView(commonRoutes.LOGIN)
    }
    // Return false so the shared TRY_UNAUTHORIZED_MODE_RESULT handler
    // still releases the render gate (INIT_COMPLETE) — there is no
    // full-page redirect to keep the loader up for.
    return false
  }

  public displayResetPasswordView(resetCode: string = '', state?: any) {
    this._displayView(
      commonRoutes.RESET_PASSWORD.replace(':resetCode', resetCode),
      { state },
    )
  }

  public displayRedirectorView = () => {
    this._displayView(commonRoutes.REDIRECTOR)
  }

  /**
   * Redirect into a specific project the portal picker resolved, rather
   * than the tenant's default landing. When `projectCode` is a valid
   * project code, synthesize a `return_to` naming that project's HOME
   * route so the Redirector's existing return_to resolution (STX1-540)
   * carries the user straight there; otherwise fall back to the plain
   * tenant redirect.
   */
  public displayProjectRedirectorView = (
    tenantHost: string,
    projectCode?: string,
  ) => {
    // On a preview login host the dev tenant opens in its preview desktop.
    const returnTo = getProjectReturnTo(
      tenantHost,
      projectCode,
      window.location.hostname,
    )
    if (returnTo) {
      const params: IDisplayViewParams = {}
      ;(params as Record<string, string>)[UrlParamKey.ReturnTo] = returnTo
      this._displayView(commonRoutes.REDIRECTOR, params)
      return
    }
    this._displayView(commonRoutes.REDIRECTOR)
  }

  public displayDeliveriesView() {
    this._displayView(commonRoutes.PORTAL)
  }

  public displayDeliveryDetailsView() {
    this._displayView(commonRoutes.PORTAL)
  }

  public displaySharedDeliveryView() {
    this._displayView(commonRoutes.PORTAL)
  }

  public openDeliveryView() {
    return
  }

  public displayPhotoView(photo: Photo) {
    void photo
    this._displayView(commonRoutes.PORTAL)
  }

  public displayNotifications() {
    this._displayView(commonRoutes.PORTAL)
  }

  public openChat() {
    return
  }

  public get defaultAvailablePage(): string {
    return commonRoutes.PORTAL
  }
}
